import { Button } from 'antd';
import { ArrowLeftOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { getTeamCompanyCard, getTeamPersonSummary } from '@/services/teamAssignments.service';
import { useAuthStore } from '@/store/useAuthStore';
import { isHlbStaffRoles } from './companyListFilter';
import { TEAM_BACK_KEY, storedBoard } from './teamBoardNav';

/**
 * "HLB team" button in the header of a user, employee or company page.
 * Reached from the board (also after moving on to the edit page) it goes back
 * to the same board view; on the profile of a team member it otherwise opens
 * the board for that person, and on the profile of a served client the board
 * opened at that firm. Shows nothing in any other case.
 */
export default function TeamBackButton({
  userId,
  employeeId,
  companyId,
  // 'profile': the pill style of the employee/company profile header.
  variant,
}) {
  const { t } = useTranslation('teamAssignments');
  const location = useLocation();
  const roles = useAuthStore((state) => state.user?.roles) || [];
  const fromState = location.state?.backTo;
  const backUrl = fromState && String(fromState).includes('/team-assignments') ? fromState : storedBoard();

  const { data: person } = useQuery({
    queryKey: ['team-person-summary', userId, employeeId],
    queryFn: () => getTeamPersonSummary({ userId, employeeId }),
    enabled: isHlbStaffRoles(roles) && !!(userId || employeeId),
    retry: false,
  });

  // Same query as the team card on the company profile, so it is shared.
  const { data: card } = useQuery({
    queryKey: ['team-card', companyId],
    queryFn: () => getTeamCompanyCard(companyId),
    enabled: isHlbStaffRoles(roles) && !!companyId,
    retry: false,
  });
  // A manager's board holds every served client; a staff member's only their own.
  const onBoard =
    card?.companies?.some((c) => c._id === String(companyId) && c.hlbClient?.status === 'ACTIVE') &&
    (card.actor?.isManager || card.assignments?.some((a) => a.employee === card.actor?.employeeId));

  // The page's own person or firm always wins over a remembered board view;
  // the remembered view only lends its other filters (function, status tab).
  const target = person?.employeeId ? ['person', person.employeeId] : onBoard ? ['company', String(companyId)] : null;
  const toBoard = ([key, value]) => {
    const base = backUrl || `${ROUTES.admin}/${ROUTES.teamAssignments}`;
    const [path, search = ''] = base.split('?');
    const params = new URLSearchParams(search);
    ['person', 'company', 'quick', 'q'].forEach((k) => params.delete(k));
    params.set(key, value);
    return `${path}?${params.toString()}`;
  };
  const url = target ? toBoard(target) : companyId || userId || employeeId ? null : backUrl;
  const isBack = !!backUrl && !target;
  if (!url) return null;

  return (
    <Link
      to={url}
      title={t(isBack ? 'links.backToBoardHint' : companyId ? 'links.openCompanyBoard' : 'links.openPersonBoard')}
      onClick={() => {
        try {
          sessionStorage.removeItem(TEAM_BACK_KEY);
        } catch {
          // nothing to clear
        }
      }}
    >
      {variant === 'profile' ? (
        <span className="employee-profile__hb">
          {isBack ? <ArrowLeftOutlined /> : <TeamOutlined />}
          {t('links.backToBoard')}
        </span>
      ) : (
        <Button shape="round" icon={isBack ? <ArrowLeftOutlined /> : <TeamOutlined />} className="btn-default">
          {t('links.backToBoard')}
        </Button>
      )}
    </Link>
  );
}
